import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Download, Extension, CheckCircle, Settings, VpnKey, NotificationsActive } from '@mui/icons-material';

function Section({ id, title, kicker, children }) {
  return (
      <section id={id} className="scroll-mt-24">
        <div className="flex items-start justify-between gap-4">
          <div>
            {kicker && (
                <p className="text-[11px] uppercase tracking-[0.18em] font-semibold text-amber-700/80 dark:text-amber-300/80">
                  {kicker}
                </p>
            )}
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
              {title}
            </h2>
          </div>
        </div>
        <div className="mt-6">{children}</div>
      </section>
  );
}

function Pill({ children }) {
  return (
      <span className="inline-flex items-center h-8 px-3 rounded-full text-sm font-semibold border border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-100">
      {children}
    </span>
  );
}

function FeatureList({ items }) {
  return (
      <ul className="mt-4 space-y-2">
        {items.map((t, idx) => (
            <li key={idx} className="flex items-start gap-2.5 text-sm text-slate-700 dark:text-slate-200">
              <CheckCircle sx={{ fontSize: 18 }} className="text-amber-600 dark:text-amber-300 mt-0.5" />
              <span className="leading-relaxed">{t}</span>
            </li>
        ))}
      </ul>
  );
}

function DownloadCard({ plugin, onJump }) {
  const handleDownload = (e) => {
    e.preventDefault();

    // Créer un lien temporaire pour forcer le téléchargement
    const link = document.createElement('a');
    link.href = plugin.downloadHref;
    link.download = ''; // Force le téléchargement
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
      <div className="group rounded-3xl border border-slate-200 bg-white shadow-[0_12px_50px_-40px_rgba(15,23,42,0.28)] overflow-hidden dark:border-slate-700/70 dark:bg-slate-900/30">
        <div className="p-6 sm:p-7">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-2xl bg-amber-500/10 text-amber-700 border border-amber-200/60 dark:border-amber-400/25 dark:text-amber-200">
                <Extension sx={{ fontSize: 22 }} />
              </span>
                <Pill>{plugin.platform}</Pill>
              </div>
              <h3 className="mt-4 text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 truncate">
                {plugin.title}
              </h3>
              <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 leading-relaxed">
                {plugin.subtitle}
              </p>
            </div>
          </div>

          <div className="mt-5 grid grid-cols-1 gap-2">
            <button
                onClick={handleDownload}
                className="h-11 inline-flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-semibold shadow-lg shadow-amber-500/15 hover:from-amber-600 hover:to-yellow-700 transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60"
                aria-label={`Télécharger ${plugin.title} (ZIP)`}
            >
              <Download sx={{ fontSize: 18 }} />
              Télécharger (.zip)
            </button>
            <button
                type="button"
                onClick={onJump}
                className="h-11 inline-flex items-center justify-center rounded-2xl border border-slate-200 text-slate-800 bg-white hover:bg-slate-50 transition-colors font-semibold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-100 dark:hover:bg-slate-800/40"
                aria-label={`Voir l'installation pour ${plugin.title}`}
            >
              Voir l'installation
            </button>
          </div>

          <div className="mt-5">
            <p className="text-[12px] font-semibold text-slate-800 dark:text-slate-200">Cas d'usage</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {plugin.useCases.map((c) => (
                  <span
                      key={c}
                      className="text-xs px-3 py-1.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200"
                  >
                {c}
              </span>
              ))}
            </div>
          </div>

          <FeatureList items={plugin.features} />
        </div>
      </div>
  );
}

function Accordion({ title, children }) {
  return (
      <details className="group rounded-2xl border border-slate-200 bg-white/70 dark:border-slate-700/70 dark:bg-slate-950/20">
        <summary className="cursor-pointer list-none px-5 py-4 flex items-center justify-between gap-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/60 rounded-2xl">
          <span className="font-semibold text-slate-900 dark:text-slate-100">{title}</span>
          <span className="text-slate-500 group-open:rotate-180 transition-transform dark:text-slate-400" aria-hidden="true">
          ▾
        </span>
        </summary>
        <div className="px-5 pb-5 pt-0 text-sm text-slate-700 dark:text-slate-200 leading-relaxed">{children}</div>
      </details>
  );
}

export default function Plugins() {
  // Configuration des chemins de fichiers ZIP
  const plugins = useMemo(
      () => [
        {
          key: 'wp-standalone',
          platform: 'WordPress',
          title: 'Netikash Payments for WordPress',
          subtitle:
              'Pour encaisser un paiement simple sans WooCommerce (formulaire ou bouton ponctuel).',
          downloadHref: '/plugins/netikash-payments-wordpress-standalone-v1.0.4.zip',
          jumpTo: 'install-wordpress',
          useCases: ['Services', 'Frais d\'inscription', 'Dons', 'Page de paiement', 'Paiement ponctuel'],
          features: [
            'Formulaire de paiement (montant libre)',
            'Devise CDF ou USD',
            'Redirection vers Netikash + page résultat client',
            'Impression de preuve de paiement',
            'Historique des paiements dans l\'admin WordPress',
            'Test de connexion OAuth',
          ],
        },
        {
          key: 'woocommerce',
          platform: 'WooCommerce',
          title: 'Netikash Gateway for WooCommerce',
          subtitle:
              'Passerelle de paiement Netikash pour les boutiques WordPress (checkout WooCommerce).',
          downloadHref: '/plugins/netikash-woocommerce-gateway-v1.2.0.zip',
          jumpTo: 'install-woocommerce',
          useCases: ['Boutique', 'Panier', 'Checkout', 'Commandes', 'Paiement commande'],
          features: [
            'Méthode de paiement Netikash au checkout',
            'Montant et devise de la commande WooCommerce',
            'Redirection vers Netikash + retour client',
            'Mise à jour du statut de commande',
            'Test de connexion OAuth',
          ],
        },
        {
          key: 'prestashop',
          platform: 'PrestaShop',
          title: 'Netikash Module for PrestaShop',
          subtitle:
              'Ajoute Netikash comme moyen de paiement dans le tunnel de commande PrestaShop.',
          downloadHref: '/plugins/netikash-prestashop-module-v1.1.0.zip',
          jumpTo: 'install-prestashop',
          useCases: ['Boutique', 'Checkout standard', 'Paiement commande', 'Callbacks'],
          features: [
            'Option de paiement Netikash dans le checkout',
            'Redirection + page de retour client',
            'Callback/webhook pour fiabiliser l\'état',
            'Mise à jour du statut de commande',
            'Test de connexion OAuth',
          ],
        },
      ],
      []
  );

  const jump = (id) => {
    try {
      document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (_) {}
  };

  return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 pt-16 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors">
        <Helmet>
          <title>Plugins - Netikash Developer</title>
          <link rel="canonical" href="https://developer.netikash.com/plugins" />
        </Helmet>

        {/* Hero */}
        <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="inline-flex items-center px-6 py-3 rounded-full bg-white/10 backdrop-blur-md border border-white/20 mb-6">
                <span className="w-3 h-3 bg-emerald-400 rounded-full mr-3 animate-pulse" />
                <span className="text-white/90 font-semibold text-lg">PLUGINS CMS</span>
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold mb-4">
                Plugins Netikash pour{' '}
                <span className="bg-gradient-to-r from-amber-200 to-yellow-200 bg-clip-text text-transparent">
                WordPress, WooCommerce et PrestaShop
              </span>
              </h1>
              <p className="text-xl text-white/85 max-w-3xl mx-auto leading-relaxed">
                Intégrez rapidement les paiements Netikash dans les principaux CMS, sans développer toute l'intégration API depuis zéro.
              </p>
              <div className="mt-7 flex flex-wrap items-center justify-center gap-2">
                <Pill>WordPress</Pill>
                <Pill>WooCommerce</Pill>
                <Pill>PrestaShop</Pill>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {plugins.map((p) => (
                <DownloadCard key={p.key} plugin={p} onJump={() => jump(p.jumpTo)} />
            ))}
          </div>

          {/* Overview */}
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <Section id="overview" title="Vue d'ensemble" kicker="en bref">
                <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20">
                  <p className="text-slate-700 dark:text-slate-200 leading-relaxed">
                    Les plugins Netikash permettent de :
                  </p>
                  <FeatureList
                      items={[
                        'Connecter une application à l\'API Netikash',
                        'Créer une demande de paiement',
                        'Rediriger le client vers la page de paiement Netikash',
                        'Récupérer le retour utilisateur',
                        'Vérifier le statut du paiement',
                        'Recevoir les callbacks/webhooks',
                      ]}
                  />
                </div>
              </Section>
            </div>

            <div className="lg:col-span-1">
              <Section id="prerequisites" title="Prérequis" kicker="avant de commencer">
                <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20">
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Settings className="text-amber-600 dark:text-amber-300" />
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-slate-100">Identifiants</p>
                        <p className="text-sm text-slate-700 dark:text-slate-200">Client ID, Client Secret</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <VpnKey className="text-amber-600 dark:text-amber-300" />
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-slate-100">OAuth URL</p>
                        <p className="text-sm text-slate-700 dark:text-slate-200">
                          <span className="font-mono text-[12px]">https://accounts.netikash.com/oauth2/token</span>
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <NotificationsActive className="text-amber-600 dark:text-amber-300" />
                      <div>
                        <p className="font-semibold text-slate-900 dark:text-slate-100">API Base URL</p>
                        <p className="text-sm text-slate-700 dark:text-slate-200">
                          <span className="font-mono text-[12px]">https://api.netikash.com</span>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </Section>
            </div>
          </div>

          {/* Installation */}
          <div className="mt-12">
            <Section id="installation" title="Installation" kicker="étapes">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-3">
                  <div id="install-wordpress" className="scroll-mt-24" />
                  <Accordion title="WordPress autonome — Netikash Payments">
                    <ol className="list-decimal ml-5 space-y-2">
                      <li>Installer le plugin ZIP dans WordPress, puis l'activer.</li>
                      <li>Aller dans <span className="font-semibold">Netikash Payments &gt; Réglages</span>.</li>
                      <li>Renseigner Client ID, Client Secret, OAuth URL et API Base URL.</li>
                      <li>Utiliser <span className="font-semibold">Tester la connexion</span>.</li>
                      <li>Créer une page WordPress et ajouter un shortcode.</li>
                    </ol>
                    <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 font-mono text-[12px] text-slate-800 dark:border-slate-700 dark:bg-slate-900/40 dark:text-slate-100">
                      [netikash_payment_form]{'\n'}
                      [netikash_payment_button amount=&quot;1500&quot; currency=&quot;CDF&quot; description=&quot;Paiement service&quot; button_text=&quot;Payer avec Netikash&quot;]
                    </div>
                  </Accordion>

                  <div id="install-woocommerce" className="scroll-mt-24" />
                  <Accordion title="WooCommerce — Netikash Gateway">
                    <ol className="list-decimal ml-5 space-y-2">
                      <li>Installer et activer WooCommerce.</li>
                      <li>Installer et activer le plugin Netikash WooCommerce.</li>
                      <li>Aller dans <span className="font-semibold">WooCommerce &gt; Settings &gt; Payments</span> et activer Netikash.</li>
                      <li>Ouvrir la configuration et renseigner les paramètres API.</li>
                      <li>Tester la connexion OAuth, puis faire une commande test.</li>
                    </ol>
                  </Accordion>

                  <div id="install-prestashop" className="scroll-mt-24" />
                  <Accordion title="PrestaShop — Netikash Module">
                    <ol className="list-decimal ml-5 space-y-2">
                      <li>Installer le module ZIP dans PrestaShop et l'activer.</li>
                      <li>Ouvrir la page de configuration du module.</li>
                      <li>Renseigner Client ID, Client Secret, OAuth URL et API Base URL.</li>
                      <li>Tester la connexion OAuth, puis effectuer une commande test.</li>
                    </ol>
                  </Accordion>
                </div>

                <div className="lg:col-span-1">
                  <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20">
                    <p className="text-slate-900 dark:text-slate-100 font-semibold">Choisir le bon plugin</p>
                    <div className="mt-4 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                      <div>
                        <p className="font-semibold">WordPress autonome</p>
                        <p>Si vous voulez un paiement simple sans boutique WooCommerce.</p>
                      </div>
                      <div>
                        <p className="font-semibold">WooCommerce</p>
                        <p>Si vous utilisez déjà WooCommerce (panier/commandes).</p>
                      </div>
                      <div>
                        <p className="font-semibold">PrestaShop</p>
                        <p>Si votre boutique fonctionne déjà sur PrestaShop.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* Flow */}
          <div className="mt-12">
            <Section id="payment-flow" title="Flux de paiement" kicker="comment ça marche">
              <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20">
                <ol className="list-decimal ml-5 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                  <li>Le client initie un paiement sur le site.</li>
                  <li>Le plugin crée une demande de paiement via l'API Netikash.</li>
                  <li>Le client est redirigé vers la page de paiement Netikash.</li>
                  <li>Le paiement est effectué sur Netikash.</li>
                  <li>Netikash redirige le client vers la page de retour.</li>
                  <li>Le plugin vérifie le statut du paiement.</li>
                  <li>Le callback/webhook peut mettre à jour le statut côté serveur.</li>
                </ol>
              </div>
            </Section>
          </div>

          {/* Webhooks */}
          <div className="mt-12">
            <Section id="webhooks" title="Redirect / Callback / Webhooks" kicker="fiabiliser l'état du paiement">
              <div className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  <div className="lg:col-span-2">
                    <p className="text-sm text-slate-700 dark:text-slate-200 leading-relaxed">
                      Pour une expérience fluide, on s'appuie généralement sur <span className="font-semibold">2 signaux</span> :
                      la <span className="font-semibold">redirection (retour client)</span> et un <span className="font-semibold">callback/webhook serveur à serveur</span>.
                      Le retour client sert à afficher un écran "succès / échec", tandis que le webhook fiabilise la mise à jour d'état en back-office.
                    </p>
                    <div className="mt-4 space-y-3">
                      <Accordion title="Redirection (return URL)">
                        <ul className="list-disc ml-5 space-y-2">
                          <li>Le client revient vers votre site après paiement.</li>
                          <li>Le plugin vérifie ensuite le statut du paiement via l'API.</li>
                          <li>À utiliser pour afficher l'interface de confirmation au client.</li>
                        </ul>
                      </Accordion>
                      <Accordion title="Callback / Webhook (server-to-server)">
                        <ul className="list-disc ml-5 space-y-2">
                          <li>Notification asynchrone envoyée vers votre serveur.</li>
                          <li>Permet de mettre à jour l'état même si le client ferme la page.</li>
                          <li>À utiliser pour synchroniser commandes/paiements dans le CMS.</li>
                        </ul>
                      </Accordion>
                    </div>
                  </div>
                  <div className="lg:col-span-1">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5 dark:border-slate-700 dark:bg-slate-900/40">
                      <p className="font-semibold text-slate-900 dark:text-slate-100">Bonnes pratiques</p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                        <li className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500/80" aria-hidden="true" />
                          <span>Garder les URLs accessibles publiquement (HTTPS).</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500/80" aria-hidden="true" />
                          <span>Vérifier l'état via API avant de marquer "payé".</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500/80" aria-hidden="true" />
                          <span>Tracer une référence unique par transaction.</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Section>
          </div>

          {/* Troubleshooting */}
          <div className="mt-12">
            <Section id="troubleshooting" title="Dépannage" kicker="diagnostic rapide">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {[
                  {
                    t: 'Le bouton ne redirige pas',
                    points: ['Configuration API', 'Client ID / Client Secret', 'Test de connexion OAuth', 'URL API Netikash'],
                  },
                  {
                    t: 'La page retour est incohérente',
                    points: ['Vérification de statut', 'Callback/webhook', 'Référence paiement', 'Réponse API Netikash'],
                  },
                  {
                    t: 'Le callback ne met pas à jour',
                    points: ['URL callback', 'Accessibilité publique', 'Réception payload', 'Correspondance des références'],
                  },
                ].map((b) => (
                    <div
                        key={b.t}
                        className="rounded-3xl border border-slate-200 bg-white/80 p-6 sm:p-7 shadow-sm dark:border-slate-700/70 dark:bg-slate-950/20"
                    >
                      <p className="font-semibold text-slate-900 dark:text-slate-100">{b.t}</p>
                      <ul className="mt-3 space-y-2 text-sm text-slate-700 dark:text-slate-200">
                        {b.points.map((p) => (
                            <li key={p} className="flex items-start gap-2">
                              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500/80" aria-hidden="true" />
                              <span>{p}</span>
                            </li>
                        ))}
                      </ul>
                    </div>
                ))}
              </div>
            </Section>
          </div>
        </div>
      </div>
  );
}